import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, ShoppingCart, Users, Headphones, Inbox, Mail, ScrollText, FileText,
  LogOut, Plus, Upload, Trash2, Eye, EyeOff, RefreshCw, CheckCircle2, Loader2, X, Cpu, Ban, Send, RotateCcw,
} from "lucide-react";

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["products", "Products", Package],
  ["orders", "Orders", ShoppingCart],
  ["customers", "Customers", Users],
  ["support", "Support", Headphones],
  ["inquiries", "Inquiries", Inbox],
  ["emails", "Emails", Mail],
  ["legal", "Legal", FileText],
  ["audit", "Audit Log", ScrollText],
];

export default function AdminDashboard() {
  const { admin, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => { if (ready && !admin) nav("/admin"); }, [ready, admin, nav]);
  if (!ready || !admin) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen flex bg-[#070B14]">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-[#1E2F4D] bg-[#0A0E1A] p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 px-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-[#2563EB] flex items-center justify-center"><Cpu className="w-5 h-5 text-white" /></div>
          <div><span className="font-display font-extrabold text-white text-sm">REVONA</span><span className="block text-[9px] font-mono tracking-widest text-cyan-400">ADMIN</span></div>
        </div>
        <nav className="space-y-1 flex-1">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === id ? "bg-[#172642] text-white" : "text-slate-400 hover:text-white hover:bg-[#111C30]"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout-btn"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-rose-400 hover:bg-[#111C30]">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </aside>

      <div className="flex-1 min-w-0">
        {/* Mobile top nav */}
        <div className="md:hidden flex items-center gap-2 overflow-x-auto p-3 border-b border-[#1E2F4D] bg-[#0A0E1A]">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${tab === id ? "bg-[#2563EB] text-white" : "bg-[#111C30] text-slate-300"}`}>{label}</button>
          ))}
          <button onClick={() => { logout(); nav("/"); }} className="px-3 py-1.5 rounded-full text-xs bg-[#111C30] text-rose-400">Exit</button>
        </div>

        <div className="p-4 sm:p-8 max-w-6xl">
          {tab === "overview" && <Overview />}
          {tab === "products" && <Products />}
          {tab === "orders" && <Orders />}
          {tab === "customers" && <Customers />}
          {tab === "support" && <Support />}
          {tab === "inquiries" && <Inquiries />}
          {tab === "emails" && <Emails />}
          {tab === "legal" && <Legal />}
          {tab === "audit" && <Audit />}
        </div>
      </div>
    </div>
  );
}

function Title({ children, action }) {
  return <div className="flex items-center justify-between mb-6"><h1 className="font-display text-2xl font-bold text-white">{children}</h1>{action}</div>;
}

// ---------------- Overview ----------------
function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  const cards = s ? [
    ["Revenue (paid)", euro(s.revenue)], ["Total orders", s.total_orders], ["Paid orders", s.paid_orders],
    ["Pending bank transfers", s.pending_bank], ["Products", s.products], ["Open support", s.open_tickets],
  ] : [];
  return (
    <div>
      <Title>Dashboard Overview</Title>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-stats">
        {cards.map(([l, v]) => (
          <div key={l} className="tactical-card p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{l}</div>
            <div className="font-display text-3xl font-extrabold text-white mt-2">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Products ----------------
const DEFAULT_PRODUCT_PRICE = 2499;

function receiptColor(status) {
  if (status === "sent") return "text-emerald-400";
  if (status === "failed") return "text-rose-400";
  if (status === "mocked") return "text-amber-400";
  return "text-slate-500";
}

const EMPTY = { title: "", category: "AI Software", price: DEFAULT_PRODUCT_PRICE, tagline: "", description: "", license: "Enterprise Licence", specs: "", published: true, instructions: "" };

function Products() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // product or "new"
  const [uploadFor, setUploadFor] = useState(null);

  const load = useCallback(() => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted"); load();
  };
  const togglePublish = async (p) => {
    await api.post(`/admin/products/${p.id}/publish?published=${!p.published}`); load();
  };

  return (
    <div>
      <Title action={
        <button onClick={() => setEditing("new")} data-testid="admin-add-product-btn"
          className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow text-white text-sm font-semibold px-4 py-2.5 rounded-full">
          <Plus className="w-4 h-4" /> Add Product
        </button>}>Products</Title>

      <div className="space-y-3" data-testid="admin-products-list">
        {items.map((p) => (
          <div key={p.id} className="tactical-card p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`admin-product-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-white">{p.title}</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/30">{p.category}</span>
                {p.published ? <span className="text-[10px] text-emerald-400">● Published</span> : <span className="text-[10px] text-slate-500">● Draft</span>}
                {p.has_file ? <span className="text-[10px] text-cyan-400">ZIP v{p.current_version}</span> : <span className="text-[10px] text-amber-400">No file</span>}
              </div>
              <div className="text-sm text-slate-400 mt-1">{euro(p.price)} · {p.versions?.length || 0} version(s)</div>
            </div>
            <div className="flex items-center gap-2">
              <IconBtn title="Upload ZIP" onClick={() => setUploadFor(p)} testid={`upload-${p.id}`}><Upload className="w-4 h-4" /></IconBtn>
              <IconBtn title={p.published ? "Unpublish" : "Publish"} onClick={() => togglePublish(p)} testid={`publish-${p.id}`}>{p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</IconBtn>
              <IconBtn title="Edit" onClick={() => setEditing(p)} testid={`edit-${p.id}`}><FileText className="w-4 h-4" /></IconBtn>
              <IconBtn title="Delete" onClick={() => del(p)} danger testid={`delete-${p.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
            </div>
          </div>
        ))}
      </div>

      {editing && <ProductModal product={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {uploadFor && <UploadModal product={uploadFor} onClose={() => setUploadFor(null)} onDone={() => { setUploadFor(null); load(); }} />}
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return <button onClick={onClick} title={title} data-testid={testid}
    className={`w-9 h-9 rounded-lg border border-[#1E2F4D] flex items-center justify-center transition-colors ${danger ? "text-rose-400 hover:border-rose-500" : "text-slate-300 hover:border-[#2563EB] hover:text-white"}`}>{children}</button>;
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0E1626] border border-[#1E2F4D] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h2 className="font-display text-lg font-bold text-white">{title}</h2><button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const [f, setF] = useState(product ? {
    title: product.title, category: product.category, price: product.price, tagline: product.tagline || "",
    description: product.description || "", license: product.license || "", specs: (product.specs || []).join(", "),
    published: product.published, instructions: product.instructions || "",
  } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    setSaving(true);
    const body = { ...f, price: parseFloat(f.price), specs: f.specs.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (product) await api.put(`/admin/products/${product.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={product ? "Edit Product" : "Add Product"} onClose={onClose}>
      <div className="space-y-3">
        <L label="Title"><input value={f.title} onChange={set("title")} data-testid="product-title-input" className={inp} /></L>
        <div className="grid grid-cols-2 gap-3">
          <L label="Category"><select value={f.category} onChange={set("category")} className={inp}>{["AI Software", "Network Tools", "Computer Software", "Digital Products"].map((c) => <option key={c}>{c}</option>)}</select></L>
          <L label="Price (EUR)"><input type="number" value={f.price} onChange={set("price")} data-testid="product-price-input" className={inp} /></L>
        </div>
        <L label="Tagline"><input value={f.tagline} onChange={set("tagline")} className={inp} /></L>
        <L label="Description"><textarea value={f.description} onChange={set("description")} rows={3} className={inp} /></L>
        <L label="Specs (comma separated)"><input value={f.specs} onChange={set("specs")} className={inp} /></L>
        <L label="License"><input value={f.license} onChange={set("license")} className={inp} /></L>
        <L label="Instructions"><textarea value={f.instructions} onChange={set("instructions")} rows={2} className={inp} /></L>
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.published} onChange={set("published")} /> Published</label>
        <button onClick={save} disabled={saving} data-testid="save-product-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full mt-2">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save
        </button>
      </div>
    </Modal>
  );
}

function UploadModal({ product, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState(`v${(product.versions?.length || 0) + 1}.0`);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) { toast.error("Choose a .zip file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("version", version);
    try {
      await api.post(`/admin/products/${product.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Package uploaded"); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setVer = async (v) => { await api.post(`/admin/products/${product.id}/set-version?version=${encodeURIComponent(v)}`); toast.success(`Active version: ${v}`); onDone(); };

  return (
    <Modal title={`Upload ZIP · ${product.title}`} onClose={onClose}>
      <div className="space-y-4">
        <L label="Version label"><input value={version} onChange={(e) => setVersion(e.target.value)} data-testid="upload-version-input" className={inp} /></L>
        <L label="Private ZIP package">
          <input type="file" accept=".zip" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file-input"
            className="w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#2563EB] file:text-white file:text-sm" />
        </L>
        <button onClick={upload} disabled={busy} data-testid="do-upload-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
        </button>
        {product.versions?.length > 0 && (
          <div className="pt-3 border-t border-[#1E2F4D]">
            <div className="text-xs text-slate-500 mb-2">Existing versions (active: {product.current_version})</div>
            <div className="space-y-2">
              {product.versions.map((v) => (
                <div key={v.version} className="flex items-center justify-between text-sm bg-[#0A0E1A] rounded-lg px-3 py-2">
                  <span className="text-slate-300">{v.version} · {v.original_filename}</span>
                  {v.version !== product.current_version && <button onClick={() => setVer(v.version)} className="text-cyan-400 text-xs">Set active</button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------- Orders ----------------
function Orders() {
  const [orders, setOrders] = useState([]);
  const load = useCallback(() => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div>
      <Title action={<button onClick={load} className="text-slate-400 hover:text-white"><RefreshCw className="w-4 h-4" /></button>}>Orders</Title>
      <div className="space-y-3" data-testid="admin-orders-list">
        {orders.length === 0 && <p className="text-slate-500 text-sm">No orders yet.</p>}
        {orders.map((o) => (
          <div key={o.id} className="tactical-card p-4" data-testid={`admin-order-${o.order_number}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-cyan-400 text-sm">{o.order_number}</span>
                  <Badge status={o.payment_status} />
                  <span className="text-[10px] font-mono text-slate-500 uppercase">{o.payment_method.replace("_", " ")}</span>
                </div>
                <div className="text-sm text-white mt-1">{o.product_title} · {euro(o.amount)}</div>
                <div className="text-xs text-slate-400">{o.customer?.full_name} · {o.customer?.email}</div>
                {o.license_key && <div className="text-xs mt-1"><span className="text-slate-500">License:</span> <span className="font-mono text-cyan-400" data-testid={`license-${o.order_number}`}>{o.license_key}</span></div>}
                <div className="text-xs text-slate-500 mt-0.5">
                  Downloads: {o.download_count}/{o.download_limit ?? 3}{o.last_download_at ? ` · last ${new Date(o.last_download_at).toLocaleString()}` : ""}
                  {" · Receipt: "}<span className={receiptColor(o.receipt_status)}>{o.receipt_status || "not_sent"}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {o.payment_method === "bank_transfer" && o.payment_status !== "paid" && (
                  <button onClick={() => act(() => api.post(`/admin/orders/${o.order_number}/confirm-bank`), "Bank transfer confirmed")}
                    data-testid={`confirm-bank-${o.order_number}`}
                    className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-2 rounded-full">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Confirm Payment
                  </button>
                )}
                {o.payment_status === "paid" && <>
                  <IconBtn title="Resend email" onClick={() => act(() => api.post(`/admin/orders/${o.order_number}/resend-email`), "Email resent")} testid={`resend-${o.order_number}`}><Send className="w-4 h-4" /></IconBtn>
                  <IconBtn title="Regenerate link (resets count)" onClick={() => act(() => api.post(`/admin/orders/${o.order_number}/regenerate-link`), "Link regenerated")} testid={`regen-${o.order_number}`}><RefreshCw className="w-4 h-4" /></IconBtn>
                  <IconBtn title="Reset download count" onClick={() => act(() => api.post(`/admin/orders/${o.order_number}/reset-downloads`), "Downloads reset")} testid={`reset-dl-${o.order_number}`}><RotateCcw className="w-4 h-4" /></IconBtn>
                  <button title="Set download limit" data-testid={`set-limit-${o.order_number}`}
                    onClick={() => { const v = window.prompt("Max downloads for this order:", o.download_limit ?? 3); const n = parseInt(v, 10); if (n >= 1) act(() => api.post(`/admin/orders/${o.order_number}/set-download-limit?limit=${n}`), `Limit set to ${n}`); }}
                    className="h-9 px-3 rounded-lg border border-[#1E2F4D] text-slate-300 hover:border-[#2563EB] hover:text-white text-xs font-medium">Limit {o.download_limit ?? 3}</button>
                  <IconBtn title={o.download_enabled ? "Disable download" : "Enable download"} danger={o.download_enabled} onClick={() => act(() => api.post(`/admin/orders/${o.order_number}/toggle-download?enabled=${!o.download_enabled}`), "Updated")} testid={`toggle-dl-${o.order_number}`}>{o.download_enabled ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}</IconBtn>
                </>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Badge({ status }) {
  const map = { paid: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", pending: "text-amber-400 bg-amber-500/10 border-amber-500/30", failed: "text-rose-400 bg-rose-500/10 border-rose-500/30" };
  return <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${map[status] || map.pending}`}>{status}</span>;
}

// ---------------- Customers ----------------
function Customers() {
  const [c, setC] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setC(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <Title>Customers</Title>
      <div className="space-y-3" data-testid="admin-customers-list">
        {c.length === 0 && <p className="text-slate-500 text-sm">No customers yet.</p>}
        {c.map((x) => (
          <div key={x.email} className="tactical-card p-4 flex items-center justify-between">
            <div><div className="text-white font-medium">{x.name}</div><div className="text-xs text-slate-400">{x.email} · {x.country}</div></div>
            <div className="text-right"><div className="text-sm text-white">{euro(x.total_paid)}</div><div className="text-xs text-slate-500">{x.orders} order(s)</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Support ----------------
function Support() {
  const [t, setT] = useState([]);
  const load = useCallback(() => api.get("/admin/support").then((r) => setT(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, s) => { await api.post(`/admin/support/${id}/status?status=${s}`); load(); };
  return (
    <div>
      <Title>Support Requests</Title>
      <div className="space-y-3" data-testid="admin-support-list">
        {t.length === 0 && <p className="text-slate-500 text-sm">No support requests.</p>}
        {t.map((x) => (
          <div key={x.id} className="tactical-card p-4">
            <div className="flex items-center justify-between"><div className="text-white font-medium">{x.name} <span className="text-xs text-slate-400 font-normal">· {x.email} · {x.phone}</span></div>
              <select value={x.status} onChange={(e) => setStatus(x.id, e.target.value)} className="bg-[#0A0E1A] border border-[#1E2F4D] rounded-lg text-xs text-white px-2 py-1">{["open", "in_progress", "closed"].map((s) => <option key={s}>{s}</option>)}</select></div>
            <div className="text-xs text-cyan-400 mt-1">{x.service_product} {x.problem && `· ${x.problem}`}</div>
            <p className="text-sm text-slate-300 mt-2">{x.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Inquiries ----------------
function Inquiries() {
  const [t, setT] = useState([]);
  useEffect(() => { api.get("/admin/inquiries").then((r) => setT(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <Title>Service Inquiries</Title>
      <div className="space-y-3" data-testid="admin-inquiries-list">
        {t.length === 0 && <p className="text-slate-500 text-sm">No inquiries.</p>}
        {t.map((x) => (
          <div key={x.id} className="tactical-card p-4">
            <div className="text-white font-medium">{x.name} <span className="text-xs text-slate-400 font-normal">· {x.email} · {x.phone}</span></div>
            <div className="text-xs text-cyan-400 mt-1">{x.service}</div>
            <p className="text-sm text-slate-300 mt-2">{x.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Emails ----------------
function Emails() {
  const [e, setE] = useState([]);
  useEffect(() => { api.get("/admin/emails").then((r) => setE(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <Title>Email Log <span className="text-xs font-mono text-amber-400">(MOCKED)</span></Title>
      <p className="text-xs text-slate-500 mb-4">Emails are mocked and logged here until a provider API key is added.</p>
      <div className="space-y-3" data-testid="admin-emails-list">
        {e.length === 0 && <p className="text-slate-500 text-sm">No emails sent yet.</p>}
        {e.map((x) => (
          <div key={x.id} className="tactical-card p-4">
            <div className="flex items-center justify-between"><span className="text-white text-sm font-medium">{x.subject}</span><span className="text-xs text-slate-500">{new Date(x.created_at).toLocaleString()}</span></div>
            <div className="text-xs text-cyan-400 mt-1">To: {x.to} · {x.order_number}</div>
            <pre className="text-xs text-slate-400 mt-2 whitespace-pre-wrap font-mono">{x.body}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Legal ----------------
function Legal() {
  const [slug, setSlug] = useState("privacy");
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const load = useCallback(() => api.get(`/legal/${slug}`).then((r) => { setContent(r.data.content); setTitle(r.data.title); }).catch(() => {}), [slug]);
  useEffect(() => { load(); }, [load]);
  const save = async () => { try { await api.put(`/admin/legal/${slug}`, { content }); toast.success("Saved"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <div>
      <Title>Legal Pages</Title>
      <div className="flex gap-2 mb-4">{["privacy", "terms", "refund"].map((s) => <button key={s} onClick={() => setSlug(s)} className={`px-4 py-2 rounded-full text-sm ${slug === s ? "bg-[#2563EB] text-white" : "bg-[#111C30] text-slate-300 border border-[#1E2F4D]"}`}>{s}</button>)}</div>
      <div className="tactical-card p-5">
        <div className="text-white font-medium mb-3">{title}</div>
        <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={14} data-testid="legal-content-input" className={inp} />
        <button onClick={save} data-testid="save-legal-btn" className="mt-4 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow text-white font-semibold px-5 py-2.5 rounded-full"><CheckCircle2 className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
}

// ---------------- Audit ----------------
function Audit() {
  const [a, setA] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setA(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <Title>Audit Log</Title>
      <div className="tactical-card divide-y divide-[#1E2F4D]" data-testid="admin-audit-list">
        {a.map((x) => (
          <div key={x.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
            <span className="font-mono text-cyan-400">{x.action}</span>
            <span className="text-slate-400 truncate max-w-[50%]">{JSON.stringify(x.detail)}</span>
            <span className="text-xs text-slate-500">{new Date(x.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const inp = "w-full bg-[#0A0E1A] border border-[#1E2F4D] rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#2563EB]";
function L({ label, children }) { return <div><label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>{children}</div>; }
